import {
  Box,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  IconButton,
  TextField,
  OutlinedInput,
  FormControl,
  Checkbox,
  ListItemText,
  Button,
} from '@mui/material';
import React from 'react';

import { IAvailability, ITimeString, IWeekDay } from '@homeporter/types';
import { COLORS } from '../../../../utils/colors';
import { useAvailableTimeStyles } from './styles';
import DeleteIcon from '../../../../icons/DeleteIcon';
import AddIcon from '@mui/icons-material/Add';

import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { days, IPlan } from '../../../../global/interfaces';

const parseTimeString = (timeStr: string): Date => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const now = new Date();
  now.setHours(hours, minutes, 0, 0);
  return now;
};

const convertTimeToDate = (time: Date | string): ITimeString => {
  if (time instanceof Date) {
    return time.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }) as ITimeString;
  } else if (typeof time === 'string') {
    const date = new Date(`1970-01-01T${time}:00`);
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }) as ITimeString;
  }
  return '-:-';
};

const canadianTimezones = [
  'America/Vancouver',
  'America/Edmonton',
  'America/Winnipeg',
  'America/Toronto',
  'America/Halifax',
  'America/St_Johns',
];

const AvailabilityHours = ({
  plans,
  services,
  setShowSave,
  selectedPlans,
  setSelectedPlans,
  availability,
  setAvailability,
  bookingLeadTime,
  setBookingLeadTime,
  bookingAvailabilityWindow,
  setBookingAvailabilityWindow,
  timezone,
  setTimezone,
}: {
  plans: IPlan[];
  services: number[];
  setShowSave: (show: boolean) => void;
  selectedPlans: number[];
  setSelectedPlans: (plans: number[]) => void;
  availability: IAvailability[];
  setAvailability: (availability: IAvailability[]) => void;
  bookingLeadTime: number;
  setBookingLeadTime: (bookingLeadTime: number) => void;
  bookingAvailabilityWindow: number;
  setBookingAvailabilityWindow: (bookingAvailabilityWindow: number) => void;
  timezone: string;
  setTimezone: (timezone: string) => void;
}) => {
  const styles = useAvailableTimeStyles();

  const handlePlansSelect = (event: any) => {
    const {
      target: { value },
    } = event;

    setShowSave(true);
    setSelectedPlans(typeof value === 'string' ? value.split(',') : value);
  };

  const addDay = (index: number = availability.length - 1) => {
    const newAvailability = [...availability];
    newAvailability.splice(index + 1, 0, { day: '', from: '-:-', to: '-:-' });

    if (index < availability.length - 1) {
      newAvailability[index + 1].day = newAvailability[index].day;
    }

    setAvailability(newAvailability);
    setShowSave(true);
  };

  const removeDay = (index: number) => {
    setAvailability(availability.filter((d, i) => i !== index));
    setShowSave(true);
  };

  return (
    <>
      <Box display={'block'} margin={'1.5rem'} marginTop={'1rem'}>
        {plans.length > 0 && (
          <>
            <Typography marginBottom={'1rem'} fontSize={'14px'}>
              Apply calendar to the following plans.
            </Typography>
            <FormControl id="select-plans" fullWidth sx={styles.formControl}>
              <Select
                multiple
                displayEmpty
                value={selectedPlans}
                onChange={handlePlansSelect}
                input={<OutlinedInput />}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return (
                      <span style={{ color: '#888', fontStyle: 'italic' }}>
                        Select Plans...
                      </span>
                    );
                  }

                  const selectedNames = plans
                    .filter((plan) => selected.includes(plan.plan_id))
                    .map((plan) => plan.name);

                  return selectedNames.join(', ');
                }}>
                {plans.map((plan) => (
                  <MenuItem key={plan.plan_id} value={plan.plan_id}>
                    <Checkbox
                      checked={selectedPlans.indexOf(plan.plan_id) > -1}
                    />
                    <ListItemText primary={plan.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        <Typography marginBottom={'1rem'} fontSize={'14px'}>
          Define the weekly days/hours that are available for booking
          appointments.
        </Typography>

        <Grid
          container
          spacing={2}
          sx={{
            width: '100%',
            margin: 0,
          }}>
          <Grid item xs={12} md={6}>
            <Typography gutterBottom>Booking Lead Time (hours)</Typography>
            <TextField
              fullWidth
              type="number"
              variant="outlined"
              value={bookingLeadTime}
              onChange={(e) => {
                setBookingLeadTime(Number(e.target.value));
                setShowSave(true);
              }}
              placeholder="Booking Lead Time"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography gutterBottom>
              Booking Availability Window (days)
            </Typography>
            <TextField
              fullWidth
              type="number"
              variant="outlined"
              value={bookingAvailabilityWindow}
              onChange={(e) => {
                setBookingAvailabilityWindow(Number(e.target.value));
                setShowSave(true);
              }}
              placeholder="Enter number of days"
            />
          </Grid>

          <Grid item xs={12} md={6} sx={{ marginBottom: '3rem' }}>
            <Select
              value={timezone}
              onChange={(e: any) => {
                setTimezone(e.target.value);
                setShowSave(true);
              }}
              onClose={() => {
                // Handle any additional logic needed when the select is closed
              }}
              sx={styles.select}>
              {canadianTimezones.map((zone) => (
                <MenuItem key={zone} value={zone}>
                  {zone}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>

        {availability.length === 0 && (
          <Box
            sx={{
              width: '100%',
              textAlign: 'right',
            }}>
            <IconButton
              sx={styles.addAvailabilityButton}
              onClick={() => addDay()}>
              <AddIcon /> ADD AVAILABILITY
            </IconButton>
          </Box>
        )}
        <Box>
          {availability.map((entry, index) => {
            return (
              <Grid
                key={index}
                container
                columnSpacing={2}
                rowSpacing={1}
                sx={styles.dayOuterGrid}>
                <Grid item xs={4} sx={styles.dayInnerGrid}>
                  <Select
                    placeholder="Select Day"
                    sx={styles.select}
                    value={entry.day || 'placeholder'}
                    onChange={(event: SelectChangeEvent<unknown>) => {
                      entry.day = event.target.value as IWeekDay;
                      setAvailability([...availability]);
                      setShowSave(true);
                    }}>
                    <MenuItem value="placeholder" disabled>
                      Select Day
                    </MenuItem>
                    {days.map((dayOfWeek) => (
                      <MenuItem
                        key={dayOfWeek}
                        value={dayOfWeek}
                        selected={dayOfWeek === entry.day}>
                        {dayOfWeek}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={3} sx={styles.flexCenter}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <TimePicker
                      label="From"
                      value={parseTimeString(entry.from)}
                      onChange={(newValue: any) => {
                        entry.from = convertTimeToDate(newValue);
                        setAvailability([...availability]);
                        setShowSave(true);
                      }}
                      ampm={true}
                      minutesStep={5}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={3} sx={styles.flexCenter}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <TimePicker
                      label="To"
                      value={parseTimeString(entry.to)}
                      onChange={(newValue: any) => {
                        entry.to = convertTimeToDate(newValue);
                        setAvailability([...availability]);
                        setShowSave(true);
                      }}
                      ampm={true}
                      minutesStep={5}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={1} sx={styles.flexCenter}>
                  <Button
                    sx={styles.addButton}
                    onClick={() => addDay(index)}
                    startIcon={<AddIcon />}>
                    Add
                  </Button>
                  <IconButton
                    sx={styles.removeButton}
                    onClick={() => removeDay(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            );
          })}
        </Box>
      </Box>
    </>
  );
};

export default AvailabilityHours;
